import styled from "@emotion/styled";
import { useEffect, useMemo, useState } from "react";

import {
  MDSBadge,
  MDSBadgeVariant,
  MDSButton,
  MDSButtonSize,
  MDSButtonSlotName,
  MDSButtonVariant,
  MDSIcon,
  MDSIconKind,
  MDSText,
  MDSTextColor,
  MDSTextField,
  MDSTextFieldVariant,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";
import {
  correctMemoryClaim,
  type MemoryEntity,
  type MemoryEntityDetail,
  type MemoryType,
  updateMemoryEntity,
} from "@/pages/agent/memory/memory-api";
import { useAppStore } from "@/store";

export const MemoryEntityDetailView = ({
  detail,
  types,
  backLabel,
  onBack,
  onChange,
  onSelectEntity,
}: {
  detail: MemoryEntityDetail;
  types: MemoryType[];
  backLabel: string;
  onBack: () => void;
  onChange: (detail: MemoryEntityDetail) => void;
  onSelectEntity: (entityId: string) => void;
}) => {
  const { store } = useAppStore();
  const [isEditing, setIsEditing] = useState(false);
  const [displayName, setDisplayName] = useState(detail.entity.display_name);
  const [summary, setSummary] = useState(detail.entity.summary);
  const [propertyKey, setPropertyKey] = useState("");
  const [displayValue, setDisplayValue] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const relationshipGroups = useMemo(
    () => groupRelationshipsByType({ relationships: detail.relationships, types }),
    [detail.relationships, types]
  );
  const hasDetails =
    Object.keys(detail.entity.properties).length > 0 || detail.claims.length > 0 || isEditing;

  useEffect(() => {
    setDisplayName(detail.entity.display_name);
    setSummary(detail.entity.summary);
  }, [detail.entity.display_name, detail.entity.summary]);

  const saveEntity = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const updated = await updateMemoryEntity({
        entityId: detail.entity.id,
        displayName: displayName.trim(),
        summary: summary.trim(),
      });
      onChange(updated);
      setIsEditing(false);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Unable to save this item");
    } finally {
      setIsSaving(false);
    }
  };

  const saveCorrection = async () => {
    if (!propertyKey.trim() || !displayValue.trim()) return;
    setIsSaving(true);
    setError(null);
    try {
      const updated = await correctMemoryClaim({
        entityId: detail.entity.id,
        propertyKey: propertyKey.trim(),
        displayValue: displayValue.trim(),
      });
      onChange(updated);
      setPropertyKey("");
      setDisplayValue("");
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Unable to save this correction");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <DetailRoot>
      <Toolbar>
        <MDSButton
          size={MDSButtonSize.Small}
          variant={MDSButtonVariant.Transparent}
          onPress={onBack}
        >
          <MDSIcon slot={MDSButtonSlotName.LeftIcon} kind={MDSIconKind.AngleLeft} />
          Back to {backLabel}
        </MDSButton>
        {!isEditing ? (
          <MDSButton
            size={MDSButtonSize.Small}
            variant={MDSButtonVariant.Chrome}
            onPress={() => setIsEditing(true)}
          >
            <MDSIcon slot={MDSButtonSlotName.LeftIcon} kind={MDSIconKind.Pen} />
            Edit
          </MDSButton>
        ) : null}
      </Toolbar>

      <DetailHeader>
        <MDSBadge
          label={detail.entity.type_display_name}
          icon={MDSIconKind.Cells}
          variant={MDSBadgeVariant.Emphasis}
        />
        {isEditing ? (
          <EditFields>
            <FieldGroup>
              <MDSText variant={MDSTextVariant.TextS} weight={MDSTextWeight.SemiBold}>
                Name
              </MDSText>
              <MDSTextField
                aria-label="Memory item name"
                value={displayName}
                variant={MDSTextFieldVariant.Form}
                onChange={setDisplayName}
              />
            </FieldGroup>
            <FieldGroup>
              <MDSText variant={MDSTextVariant.TextS} weight={MDSTextWeight.SemiBold}>
                Summary
              </MDSText>
              <MDSTextField
                aria-label="Memory item summary"
                value={summary}
                variant={MDSTextFieldVariant.Form}
                isTextArea
                textAreaProps={{ rows: 4 }}
                onChange={setSummary}
              />
            </FieldGroup>
            <ButtonRow>
              <MDSButton
                size={MDSButtonSize.Small}
                variant={MDSButtonVariant.Transparent}
                onPress={() => {
                  setDisplayName(detail.entity.display_name);
                  setSummary(detail.entity.summary);
                  setIsEditing(false);
                }}
              >
                Cancel
              </MDSButton>
              <MDSButton
                size={MDSButtonSize.Small}
                variant={MDSButtonVariant.Emphasis}
                isDisabled={!displayName.trim()}
                isLoading={isSaving}
                onPress={saveEntity}
              >
                Save changes
              </MDSButton>
            </ButtonRow>
          </EditFields>
        ) : (
          <>
            <MDSText as="h1" variant={MDSTextVariant.HeaderXl}>
              {detail.entity.display_name}
            </MDSText>
            <MDSText color={MDSTextColor.Secondary} variant={MDSTextVariant.ParagraphSm}>
              {detail.entity.summary || "Mem Agent has not written a summary yet."}
            </MDSText>
          </>
        )}
        <MetaRow>
          <MDSText color={MDSTextColor.Tertiary} variant={MDSTextVariant.TextXs}>
            {detail.entity.evidence_count} source{detail.entity.evidence_count === 1 ? "" : "s"}
          </MDSText>
          {detail.entity.related_entity_count > 0 ? (
            <MDSText color={MDSTextColor.Tertiary} variant={MDSTextVariant.TextXs}>
              {formatRelatedEntityCounts(detail.entity)}
            </MDSText>
          ) : null}
          <MDSText color={MDSTextColor.Tertiary} variant={MDSTextVariant.TextXs}>
            Last seen {formatDate(detail.entity.last_seen_at)}
          </MDSText>
        </MetaRow>
      </DetailHeader>

      {error ? (
        <ErrorText color={MDSTextColor.Danger} variant={MDSTextVariant.TextS}>
          {error}
        </ErrorText>
      ) : null}

      {hasDetails ? (
        <Section>
          <SectionHeadingRow>
            <SectionTitle as="h2" variant={MDSTextVariant.HeaderS}>
              Details
            </SectionTitle>
            {isEditing ? (
              <MDSText color={MDSTextColor.Tertiary} variant={MDSTextVariant.TextXs}>
                Your corrections take precedence over extracted values
              </MDSText>
            ) : null}
          </SectionHeadingRow>
          {Object.keys(detail.entity.properties).length > 0 ? (
            <PropertyGrid>
              {Object.entries(detail.entity.properties).map(([key, value]) => (
                <PropertyCard key={key}>
                  <MDSText color={MDSTextColor.Tertiary} variant={MDSTextVariant.TextXs}>
                    {humanizeKey(key)}
                  </MDSText>
                  <MDSText variant={MDSTextVariant.TextS}>{formatValue(value)}</MDSText>
                </PropertyCard>
              ))}
            </PropertyGrid>
          ) : null}
          {detail.claims.length > 0 ? (
            <ClaimList>
              {detail.claims.map(claim => (
                <ClaimRow key={claim.id}>
                  <ClaimCopy>
                    <MDSText color={MDSTextColor.Tertiary} variant={MDSTextVariant.TextXs}>
                      {humanizeKey(claim.property_key)}
                    </MDSText>
                    <MDSText variant={MDSTextVariant.TextS}>{claim.display_value}</MDSText>
                  </ClaimCopy>
                  <MDSBadge
                    label={claim.provenance === "user" ? "Corrected by you" : "From notes"}
                    variant={
                      claim.provenance === "user"
                        ? MDSBadgeVariant.Emphasis
                        : MDSBadgeVariant.Default
                    }
                  />
                </ClaimRow>
              ))}
            </ClaimList>
          ) : null}
          {isEditing ? (
            <CorrectionForm>
              <MDSText variant={MDSTextVariant.TextS} weight={MDSTextWeight.SemiBold}>
                Add or correct a detail
              </MDSText>
              <CorrectionFields>
                <MDSTextField
                  aria-label="Detail to correct"
                  placeholder="Detail, like rating or occasion"
                  value={propertyKey}
                  variant={MDSTextFieldVariant.Form}
                  onChange={setPropertyKey}
                />
                <MDSTextField
                  aria-label="Corrected value"
                  placeholder="Correct value"
                  value={displayValue}
                  variant={MDSTextFieldVariant.Form}
                  onChange={setDisplayValue}
                />
                <MDSButton
                  size={MDSButtonSize.Small}
                  variant={MDSButtonVariant.Emphasis}
                  isDisabled={!propertyKey.trim() || !displayValue.trim()}
                  isLoading={isSaving}
                  onPress={saveCorrection}
                >
                  Save correction
                </MDSButton>
              </CorrectionFields>
            </CorrectionForm>
          ) : null}
        </Section>
      ) : null}

      {detail.relationships.length > 0 ? (
        <Section>
          <SectionTitle as="h2" variant={MDSTextVariant.HeaderS}>
            Related
          </SectionTitle>
          <RelationshipGroups>
            {relationshipGroups.map(group => (
              <RelationshipGroup key={group.typeKey}>
                <RelationshipGroupHeader>
                  <MDSText variant={MDSTextVariant.TextS} weight={MDSTextWeight.SemiBold}>
                    {group.pluralDisplayName}
                  </MDSText>
                  <MDSBadge label={String(group.relationships.length)} />
                </RelationshipGroupHeader>
                <RelationshipGrid>
                  {group.relationships.map(relationship => (
                    <RelationshipCard
                      key={relationship.id}
                      type="button"
                      onClick={() => onSelectEntity(relationship.related_entity.id)}
                    >
                      <RelationshipIcon>
                        <MDSIcon kind={MDSIconKind.Cells} size={14} />
                      </RelationshipIcon>
                      <RelationshipCopy>
                        <MDSText variant={MDSTextVariant.TextS} weight={MDSTextWeight.SemiBold}>
                          {relationship.related_entity.display_name}
                        </MDSText>
                        {relationship.related_entity.summary || relationship.summary ? (
                          <MDSText
                            color={MDSTextColor.Secondary}
                            variant={MDSTextVariant.TextXs}
                            numberOfLines={2}
                          >
                            {relationship.related_entity.summary || relationship.summary}
                          </MDSText>
                        ) : null}
                      </RelationshipCopy>
                      <MDSIcon kind={MDSIconKind.AngleRight} size={12} />
                    </RelationshipCard>
                  ))}
                </RelationshipGrid>
              </RelationshipGroup>
            ))}
          </RelationshipGroups>
        </Section>
      ) : null}

      <Section>
        <SectionTitle as="h2" variant={MDSTextVariant.HeaderS}>
          Source notes
        </SectionTitle>
        {detail.evidence.length > 0 ? (
          <EvidenceList>
            {detail.evidence.map(evidence => (
              <EvidenceRow key={evidence.id}>
                <EvidenceCopy>
                  <MDSText variant={MDSTextVariant.ParagraphS}>“{evidence.excerpt}”</MDSText>
                  <MDSText color={MDSTextColor.Tertiary} variant={MDSTextVariant.TextXs}>
                    {evidence.source_title || "Untitled note"}
                    {evidence.source_updated_at
                      ? ` · ${formatDate(evidence.source_updated_at)}`
                      : ""}
                  </MDSText>
                </EvidenceCopy>
                {evidence.source_kind === "note" ? (
                  <MDSButton
                    size={MDSButtonSize.Small}
                    variant={MDSButtonVariant.Transparent}
                    onPress={() => {
                      store.navigation.goToNote({
                        noteId: evidence.source_id,
                        openInSameTab: false,
                      });
                    }}
                  >
                    Open note
                    <MDSIcon
                      slot={MDSButtonSlotName.RightIcon}
                      kind={MDSIconKind.ArrowUpRightFromSquare}
                    />
                  </MDSButton>
                ) : null}
              </EvidenceRow>
            ))}
          </EvidenceList>
        ) : (
          <EmptyCopy color={MDSTextColor.Secondary} variant={MDSTextVariant.TextS}>
            No active source excerpts remain for this item.
          </EmptyCopy>
        )}
      </Section>
    </DetailRoot>
  );
};

const humanizeKey = (key: string): string =>
  key
    .replaceAll("_", " ")
    .replaceAll("-", " ")
    .replace(/^./, character => character.toUpperCase());

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined) return "—";
  if (Array.isArray(value)) return value.map(formatValue).join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const formatDate = (value: string): string =>
  new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "numeric" }).format(
    new Date(value)
  );

const formatRelatedEntityCounts = (entity: MemoryEntity): string => {
  const typeSummary = entity.related_entity_type_counts
    .map(relatedType => {
      const label =
        relatedType.count === 1
          ? relatedType.type_display_name
          : relatedType.type_plural_display_name;
      return `${relatedType.count} ${label}`;
    })
    .join(" · ");

  if (typeSummary) return typeSummary;
  return `${entity.related_entity_count} related item${entity.related_entity_count === 1 ? "" : "s"}`;
};

const groupRelationshipsByType = ({
  relationships,
  types,
}: {
  relationships: MemoryEntityDetail["relationships"];
  types: MemoryType[];
}) => {
  const typesByKey = new Map(types.map(type => [type.key, type]));
  const groupsByType = new Map<
    string,
    {
      typeKey: string;
      pluralDisplayName: string;
      relationships: MemoryEntityDetail["relationships"];
    }
  >();

  relationships.forEach(relationship => {
    const relatedEntity = relationship.related_entity;
    const type = typesByKey.get(relatedEntity.type_key);
    const group = groupsByType.get(relatedEntity.type_key);

    if (group) {
      group.relationships.push(relationship);
      return;
    }

    groupsByType.set(relatedEntity.type_key, {
      typeKey: relatedEntity.type_key,
      pluralDisplayName: type?.plural_display_name ?? `${relatedEntity.type_display_name}s`,
      relationships: [relationship],
    });
  });

  return [...groupsByType.values()].sort((left, right) => {
    const countDifference = right.relationships.length - left.relationships.length;
    if (countDifference !== 0) return countDifference;
    return left.pluralDisplayName.localeCompare(right.pluralDisplayName);
  });
};

const DetailRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[7]};
  margin: 0 auto;
  max-width: 920px;
  width: 100%;
`;

const Toolbar = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
`;

const DetailHeader = styled.header`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
  max-width: 760px;
`;

const MetaRow = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const EditFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
  width: min(680px, 100%);
`;

const FieldGroup = styled.label`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const ButtonRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
  justify-content: flex-end;
`;

const ErrorText = styled(MDSText)`
  background: ${({ theme }) => theme.colors.background.input.chrome.default};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  padding: ${({ theme }) => theme.spacing[3]};
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const SectionHeadingRow = styled.div`
  align-items: baseline;
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[3]};
  justify-content: space-between;
`;

const SectionTitle = styled(MDSText)``;

const PropertyGrid = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing[3]};
  grid-template-columns: repeat(3, minmax(0, 1fr));

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const PropertyCard = styled.div`
  background: ${({ theme }) => theme.colors.background.input.chrome.default};
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
  padding: ${({ theme }) => theme.spacing[3]};
`;

const ClaimList = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  border-radius: ${({ theme }) => theme.borderRadius.xlarge};
  overflow: hidden;
`;

const ClaimRow = styled.div`
  align-items: center;
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};

  & + & {
    border-top: 1px solid ${({ theme }) => theme.colors.border.surface};
  }
`;

const ClaimCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const CorrectionForm = styled.div`
  background: ${({ theme }) => theme.colors.background.input.chrome.default};
  border-radius: ${({ theme }) => theme.borderRadius.xlarge};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[4]};
`;

const CorrectionFields = styled.div`
  align-items: flex-start;
  display: grid;
  gap: ${({ theme }) => theme.spacing[2]};
  grid-template-columns: minmax(160px, 0.65fr) minmax(220px, 1fr) auto;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const RelationshipGrid = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing[3]};
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const RelationshipGroups = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[5]};
`;

const RelationshipGroup = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const RelationshipGroupHeader = styled.div`
  align-items: center;
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const RelationshipCard = styled.button`
  align-items: flex-start;
  background: ${({ theme }) => theme.colors.background.input.chrome.default};
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  color: inherit;
  cursor: pointer;
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[4]};
  text-align: left;
  width: 100%;

  &:hover,
  &:focus-visible {
    background: ${({ theme }) => theme.colors.background.list.hover};
    outline: none;
  }
`;

const RelationshipIcon = styled.span`
  align-items: center;
  background: ${({ theme }) => theme.colors.background.button.toggle.default};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  display: flex;
  flex: 0 0 auto;
  height: 28px;
  justify-content: center;
  width: 28px;
`;

const RelationshipCopy = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const EvidenceList = styled.div`
  background: ${({ theme }) => theme.colors.background.input.chrome.default};
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  border-radius: ${({ theme }) => theme.borderRadius.xlarge};
  overflow: hidden;
`;

const EvidenceRow = styled.div`
  align-items: flex-start;
  display: flex;
  gap: ${({ theme }) => theme.spacing[4]};
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing[4]};

  & + & {
    border-top: 1px solid ${({ theme }) => theme.colors.border.surface};
  }
`;

const EvidenceCopy = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
  min-width: 0;
`;

const EmptyCopy = styled(MDSText)`
  background: ${({ theme }) => theme.colors.background.input.chrome.default};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  padding: ${({ theme }) => theme.spacing[4]};
`;
